import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { InlineBlinkingLiveDot } from 'components/InlineBlinkingLiveDot';
import { LinkIfHref } from 'components/Link/LinkIfHref';
import { isTelemundoVertical } from 'lib/vertical';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { package as PackagePropType } from 'lib/CustomPropTypes';
import {
  BAY_AREA_PID,
  BOSTON_PID,
  CHICAGO_PID,
  CONNECTICUT_PID,
  DFW_PID,
  LA_PID,
  MIAMI_PID,
  NEW_YORK_PID,
  PHILADELPHIA_PID,
  SD_PID,
  DC_PID,
  BREAKING_SLATE,
  LIVE_VIDEO_PID_MAP,
  STREAM_KEYS,
  STREAM_TO_SLATE_MAP,
  getPlayerKeyForPID,
  getPlaymakerMetadata,
  getDefaultMetadata as getDefaultPlaymakerMetadata,
} from 'lib/liveVideo';
import { getFastChannelStreamNameMap } from 'lib/fastChannelStreamNameMap';
import { isVideoLive } from 'lib/videoUtils';
import { VerticalContext, BreakpointContext, FeatureFlagContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import {
  LIVE_VIDEO_EMBED_LOGO,
  PLAYMAKER_ONLY_LIVE_LOGO,
} from 'lib/brandFeatures';
import { useBroadcastScheduleData } from 'lib/Hooks/useBroadcastScheduleData';
import { isIframeVideo } from 'lib/isIframeVideo';

import bayAreaHeaderImage from 'assets/images/local/fast-channel/bay-area-fast-channel-description-image-black-letters.svg';
import bostonHeaderImage from 'assets/images/local/fast-channel/boston-fast-channel-description-image-black-letters.svg';
import chicagoHeaderImage from 'assets/images/local/fast-channel/chicago-fast-channel-description-image-black-letters.svg';
import connecticutHeaderImage from 'assets/images/local/fast-channel/connecticut-fast-channel-description-image-black-letters.svg';
import dallasHeaderImage from 'assets/images/local/fast-channel/dallas-fast-channel-description-image-black-letters.svg';
import laHeaderImage from 'assets/images/local/fast-channel/los-angeles-fast-channel-description-image-black-letters.svg';
import newYorkHeaderImage from 'assets/images/local/fast-channel/new-york-fast-channel-description-image-black-letters.svg';
import philadelphiaHeaderImage from 'assets/images/local/fast-channel/philadelphia-fast-channel-description-image-black-letters.svg';
import sdHeaderImage from 'assets/images/local/fast-channel/san-diego-fast-channel-description-image-black-letters.svg';
import southFloridaHeaderImage from 'assets/images/local/fast-channel/south-florida-fast-channel-description-image-black-letters.svg';
import dcHeaderImage from 'assets/images/local/fast-channel/washington-dc-fast-channel-description-image-black-letters.svg';

import { ERROR_TYPES } from 'components/TVE/Slate';
import WithTVEAuthProvider from 'components/TVE/AuthProvider';
import TempPass from 'components/TVE/TempPass';
import { encodeWindowUrlToJson, encodedXumoDefaultParams } from 'lib/encodeXumoParams';
import {
  hideProviderSelectAction,
  showProviderSelectAction,
  loadScheduleAction as loadTVEScheduleAction,
} from 'redux/modules/tve';

import { getLogoForStreamKey } from './Logos';
import NDPVideo from './NDPVideo';
import { CoreVideoPlayerEmbed } from './CoreVideoPlayerEmbed';
import { MSNBCMetadata } from './MSNBCMetadata';
import './styles.themed.scss';

const block = 'live-video-embed';

// exporting for testing purposes
  /**
 * buildXumoStreamUrl
 * @param {string} streamkey The base stream key URL for the channel.
 * @returns {string} The full URL with `&xumoOverride=` plus either the window override or the default params.
 */
  export function buildXumoStreamUrl(streamkey) {
    const windowOverride = encodeWindowUrlToJson();
    const override = windowOverride || encodedXumoDefaultParams;
    return `${streamkey}&xumoOverride=${override}`;
  }

/**
 * LiveVideoEmbed Package
 * @param {object} props React props
 * @param {CurationPackageContent} props.content Package content from BentoAPI
 * @param {string} props.pkgClassName default block level CSS identifier
 * @returns {React.Element} LiveVideoEmbed component
 */
function LiveVideoEmbed({
  content,
  authenticated = null,
  hasTempPass = false,
  hideProviderSelect = Function.prototype,
  shouldRenderByBreakpoint: propShouldRenderByBreakpoint = null,
  pkgClassName = 'pkg live-video-embed',
  providerSelectVisible = false,
  hasCurrentShow = true,
  showProviderSelect = Function.prototype,
  videoError = false,
  currentTVEProgram = {},
  loadTVESchedule = Function.prototype,
}) {
  const vertical = React.useContext(VerticalContext);
  const {
    'msnbc-feature-flag-logo-update': isMsnbcLogoUpdateEnabled,
    'show-local-staging-streams': shouldShowStagingStreamForLocalChannels,
    'xumo-default-params': provideDefaultXumoParams,
  } = React.useContext(FeatureFlagContext);
  const { isSorM, isLorXL, hasCheckedViewportSize } = React.useContext(BreakpointContext);
  const [checkTempPass, setCheckTempPass] = React.useState(false);
  const iframeRef = React.useRef(null);

  const { t } = useTranslation();

  // Get the correct channel map based on vertical
  const fastChannelMap = getFastChannelStreamNameMap(
    vertical,
    shouldShowStagingStreamForLocalChannels,
  );


  const localStreamUrlMap = {
    [BAY_AREA_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.bayarea?.streamKey) : fastChannelMap.bayarea?.streamKey,
      headerImage: bayAreaHeaderImage,
      videoTitle: 'NBC Bay Area',
    },
    [BOSTON_PID]: {
      streamKey: fastChannelMap.boston?.streamKey,
      headerImage: bostonHeaderImage,
      videoTitle: 'NBC10 Boston',
    },
    [CHICAGO_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.chicago?.streamKey) : fastChannelMap.chicago?.streamKey,
      headerImage: chicagoHeaderImage,
      videoTitle: 'NBC 5 Chicago',
    },
    [CONNECTICUT_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.connecticut?.streamKey) : fastChannelMap.connecticut?.streamKey,
      headerImage: connecticutHeaderImage,
      videoTitle: 'NBC Connecticut',
    },
    [DFW_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.dallasFortWorth?.streamKey) : fastChannelMap.dallasFortWorth?.streamKey,
      headerImage: dallasHeaderImage,
      videoTitle: 'NBC 5 Dallas-Fort Worth',
    },
    [LA_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.la?.streamKey) : fastChannelMap.la?.streamKey,
      headerImage: laHeaderImage,
      videoTitle: 'NBC4 Los Angeles',
    },
    [MIAMI_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.southFlorida?.streamKey) : fastChannelMap.southFlorida?.streamKey,
      headerImage: southFloridaHeaderImage,
      videoTitle: 'NBC6 South Florida',
    },
    [NEW_YORK_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.nyc?.streamKey) : fastChannelMap.nyc?.streamKey,
      headerImage: newYorkHeaderImage,
      videoTitle: 'NBC 4 New York',
    },
    [PHILADELPHIA_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.philly?.streamKey) : fastChannelMap.philly?.streamKey,
      headerImage: philadelphiaHeaderImage,
      videoTitle: 'NBC10 Philadelphia',
    },
    [SD_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.sd?.streamKey) : fastChannelMap.sd?.streamKey,
      headerImage: sdHeaderImage,
      videoTitle: 'NBC 7 San Diego',
    },
    [DC_PID]: {
      streamKey: provideDefaultXumoParams ? buildXumoStreamUrl(fastChannelMap.dc?.streamKey) : fastChannelMap.dc?.streamKey,
      headerImage: dcHeaderImage,
      videoTitle: 'NBC4 Washington',
    },
  };

  const {
    publicRuntimeConfig: { DISABLE_AUTH_TVE_MSNBC: bypassAuth },
  } = getConfig();

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const {
    isRailLayout,
    isRailAdjacent,
    pageRoute,
    pageType,
  } = (content?.context) || {};

  const { items: [item] } = content;
  const isRail = isRailLayout && !isRailAdjacent;
  const isNews = vertical === 'news';
  const isTelemundo = isTelemundoVertical(vertical);
  const isCoverPage = pageType === 'cover';
  const isSportsFront = pageRoute === '/sports';

  const packageMetadata = (content?.metadata) || {};
  const playmakerMetadata = getPlaymakerMetadata({
    ...packageMetadata,
    // for LiveVideoEmbed package, playmaker should be 'on' by default
    playmakerToggle: ('playmakerToggle' in packageMetadata)
      ? packageMetadata.playmakerToggle
      : true,
  }, vertical, '2007524');

  const liveVideoPID = LIVE_VIDEO_PID_MAP[packageMetadata?.playmakerId];
  const liveVideoPIDOverride = LIVE_VIDEO_PID_MAP[packageMetadata?.playmakerIdOverride];

  const isIframe = isIframeVideo(packageMetadata?.playmakerId, packageMetadata?.playmakerIdOverride);

  const iframeVideo = packageMetadata?.playmakerId === 'other'
    ? liveVideoPIDOverride
    : liveVideoPID;

  const isCoreVideoPlayer = packageMetadata?.playmakerToggle ?? true;

  const isEnabledOnMobileAndTablet = isSorM && (packageMetadata?.displayOnMobileAndTablet ?? true);
  const isEnabledOnNewsCover = isEnabledOnMobileAndTablet && isNews && isCoverPage;
  const isEnabledOnSports = isEnabledOnMobileAndTablet && isNews && isSportsFront;
  const isEnabledOnTelemundoCovers = isEnabledOnMobileAndTablet && isTelemundo && isCoverPage;

  // playmaker metadata
  const playmakerId = (playmakerMetadata?.playmakerIdOverride)
    || (playmakerMetadata?.playmakerId);
  let destinationPageUrl = playmakerMetadata?.destinationPageUrl;
  let dekText = playmakerMetadata?.playmakerDescription;
  let eyebrowText = (playmakerMetadata?.playmakerFlag) || t('Live');

  // video metadata
  const showDek = item?.metadata?.showDek ?? true;
  let isLiveNDPVideo = false;

  const playmakerStreamType = getPlayerKeyForPID(playmakerId);

  const isCustomStream = (packageMetadata?.playmakerId) === 'other';
  const playmakerBrand = isCustomStream
    ? playmakerMetadata?.playmakerBrandOverride
    : playmakerStreamType;

  const playmakerOnlyLiveLogo = getFeatureConfigForBrand(
    PLAYMAKER_ONLY_LIVE_LOGO,
    vertical,
  );

  const branding = isCoreVideoPlayer
    ? playmakerBrand
    : (item?.metadata?.brand) || STREAM_KEYS.NEWS_NOW;

  const displayLogo = getFeatureConfigForBrand(
    LIVE_VIDEO_EMBED_LOGO,
    vertical,
  );

  let Logo = null;

  if (displayLogo) {
    Logo = getLogoForStreamKey(branding);
  }

  const isTodayAllDay = branding === STREAM_KEYS.TODAY_ALL_DAY;
  const isMSNBCTVE = branding === STREAM_KEYS.MSNBC_TVE;
  const isNewsNOW = branding === STREAM_KEYS.NEWS_NOW;
  const isNoticiasAhora = branding === STREAM_KEYS.NOTICIAS_AHORA;
  const isTelemundoAlDia = branding === STREAM_KEYS.TELEMUNDO_AL_DIA;
  const isTelemundoDeportesAhora = branding === STREAM_KEYS.TELEMUNDO_DEPORTES_AHORA;
  const isSportsNow = branding === STREAM_KEYS.SPORTS;

  const { currentShow } = hasCurrentShow ? useBroadcastScheduleData(branding) : '';
  const playmakerTitleOverride = packageMetadata?.playmakerTitle;
  const playmakerTitleDefault = getDefaultPlaymakerMetadata(playmakerId)?.playmakerTitle;
  let headlineText = playmakerTitleOverride // use override if provided from curator
    || currentShow?.title // otherwise use current show title from schedule data
    || playmakerTitleDefault; // or finally fall back to default

  if (!isCoreVideoPlayer) {
    isLiveNDPVideo = item && isVideoLive(item.item);
    dekText = item?.computedValues?.dek;
    eyebrowText = (item?.computedValues?.unibrow?.text) || (isTodayAllDay ? 'Watch' : 'Live');
    headlineText = item?.computedValues?.headline;
    destinationPageUrl = item?.item?.url?.canonical;
  }

  const displayDek = (isCoreVideoPlayer && dekText) || (!isCoreVideoPlayer && showDek && dekText);

  const authWithProvider = authenticated && !hasTempPass;
  const msnbcScheduleDataTitle = currentTVEProgram?.title;
  const msnbcTitle = headlineText || msnbcScheduleDataTitle;
  const isMSNBCLiveVideo = isMSNBCTVE && isCoreVideoPlayer;
  const titleToDisplay = isMSNBCLiveVideo ? msnbcTitle : headlineText;

  const shouldRenderByBreakpoint = propShouldRenderByBreakpoint
    ?? (hasCheckedViewportSize && (isLorXL || isEnabledOnSports || isEnabledOnNewsCover || isEnabledOnTelemundoCovers));

  React.useEffect(() => {
    // Load vertical schedule
    if (
      shouldRenderByBreakpoint
      && isMSNBCLiveVideo
      // we want to load the schedules if specifically `msnbcScheduleDataTitle` is falsey, meaning
      // it probably hasn't been loaded yet.
      && !msnbcScheduleDataTitle
    ) {
      loadTVESchedule(vertical);
    }
  }, [shouldRenderByBreakpoint, isMSNBCLiveVideo, msnbcScheduleDataTitle]);

  // User not authenticated with mvpd
  React.useEffect(() => {
    if (shouldRenderByBreakpoint && isMSNBCLiveVideo) {
      // authenticated has three states - null, false, & true
      if (authenticated === false && !hasTempPass) {
        // render tempPass component
        setCheckTempPass(true);
      } else if (authenticated && !hasTempPass && providerSelectVisible) {
        // Close MVPD overlay
        hideProviderSelect();
      }
    }
  }, [
    authenticated,
    hasTempPass,
    providerSelectVisible,
    shouldRenderByBreakpoint,
    isMSNBCLiveVideo,
  ]);

  /* Window Media Size Tracking
  *
  *   Editorial wants the live video to move from cover zone C (topmost rail)
  *   to the zone B to the right on mobile sizes (< 1000px tablet & mobile).
  *
  *   The solution chosen is to move the Live Video Embed section element
  *   to Zone B when in mobile it in the DOM based on js detected media size.
  *   The callbacks and useEffects are arranged so that the move is done
  *   only when the media size changes to "mobile" or "larger".
  *
  *   Controlled by `movePlayerInMobile` and `mobilePlayerPosition` from content?.metadata
  */

  let movePlayerInMobile = false;
  const mobilePlayerPosition = Number(content?.metadata?.mobilePlayerPosition ?? 0);
  if (
    vertical === 'news'
    && pageType === 'cover'
    && !Number.isNaN(mobilePlayerPosition)
    && mobilePlayerPosition !== 0
  ) {
    movePlayerInMobile = true;
  }

  // lveRef - assigned to the LiveVideoEmbed section element so it can be moved.
  const lveRef = useRef(null);
  // mediaSize - current media size name, either "mobile" or "larger"
  const [mediaSize, setMediaSize] = useState('unknown');
  // homeSiblingElId - unique id for home element in case there is another LVE
  const [homeSiblingId, setHomeSiblingId] = useState(null);

  /* getWindowMediaSize() - returns a string media size name */
  const getWindowMediaSize = useCallback(() => {
    // We're using this to determine Tablet+Mobile, "mobile" size vs "larger".
    // See also `src/assets/styles/utils/_sharedvariables.scss` for definitions.
    const windowWidth = window.innerWidth;
    if (windowWidth >= 1000) {
      return 'larger';
    }
    return 'mobile';
  }, []);

  /* handleSizeChange - callback to handle resize event to only setMediaSize if it changes */
  const handleSizeChange = useCallback(() => {
    const currentMediaSize = getWindowMediaSize();
    if (currentMediaSize !== mediaSize) {
      setMediaSize(getWindowMediaSize());
    }
  }, [setMediaSize, getWindowMediaSize]);

  /* Add Event Listener for window resize. */
  useEffect(() => {
    if (movePlayerInMobile === false) return;
    window.addEventListener('resize', handleSizeChange);
    // eslint-disable-next-line consistent-return
    return (() => {
      window.removeEventListener('resize', handleSizeChange);
    });
  }, [handleSizeChange]);

  /* Initialize media size after server and first browser render */
  useEffect(() => {
    if (movePlayerInMobile === false) return;
    const mSize = getWindowMediaSize();
    if (mSize !== mediaSize) {
      setMediaSize(mSize);
    }
    setHomeSiblingId(`live-video-embed-home-sibling-${uuid()}`);
  }, []);

  /* Move LiveVideoEmbed element in DOM
      This useEffect will run only when mediaSize changes. On first call mediaSize
      is 'unknown', so it will run once. This does nothing since the conditionals cover
      only "mobile" and "larger" and is otherwise nop.
  */
  useEffect(() => {
    if (movePlayerInMobile === false) return;
    // LiveVideoEmbed element
    const lveEl = lveRef.current;
    // Handle new mediaSize, MOVE Live Video Embed section element
    if (mediaSize === 'mobile') {
      // From "larger" to "mobile"
      // LVE Element should be moved to mobile location
      const railContainerEl = lveEl?.closest('.rail__container');
      const mobileSpotParentEl = railContainerEl?.firstElementChild;
      const mobileSpotFirstSiblingEl = mobileSpotParentEl?.firstElementChild;
      const mobileSpotSiblingEls = mobileSpotParentEl?.querySelectorAll('.pkg') ?? [];
      const mobileSpotSiblings = [...mobileSpotSiblingEls];
      // Place somewhere in Zone B content.
      if (mobilePlayerPosition <= 1) {
        mobileSpotFirstSiblingEl?.before(lveEl);
      } else {
        const afterIndex = Math.min(mobilePlayerPosition - 2, mobileSpotSiblings.length - 1);
        const prevSiblingEl = mobileSpotSiblings[afterIndex >= 0 ? afterIndex : 0];
        prevSiblingEl?.after(lveEl);
      }
    } else if (mediaSize === 'larger') {
      // From "mobile" to "larger"
      // LVE Element should be moved to home location
      const homeContainerEl = document.getElementById(homeSiblingId);
      if (homeContainerEl) {
        homeContainerEl?.after(lveEl);
      }
    }
  }, [mediaSize, homeSiblingId]);

  /*                                */
  /*                                */
  /* End Window Media Size Tracking */

  const pidNumber = packageMetadata?.playmakerId !== 'other'
    ? packageMetadata.playmakerId
    : packageMetadata?.playmakerIdOverride;

  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = localStreamUrlMap[pidNumber]?.streamKey;
    script.async = true;

    const playerContainer = document.querySelector('.local-player-container');

    playerContainer?.appendChild(script);
  }, []);

  if (isIframe) {
    const headerImage = localStreamUrlMap[pidNumber]?.headerImage;
    const videoTitle = localStreamUrlMap[pidNumber]?.videoTitle;
    const isBreaking = packageMetadata?.playmakerArt?.includes('breaking');
    const slate = isBreaking ? BREAKING_SLATE : STREAM_TO_SLATE_MAP[iframeVideo];

    return (
      <div className="local-player-title-logo-container">
        <img className="local-channel-logo" alt="local-channel-logo" src={headerImage} />
        <div className="local-player-container">
          <img
            src={slate}
            alt=""
            className="iframe-slate"
          />
        </div>
        <h3>
          <span>&#8226; Live / </span>
          <span>{videoTitle}</span>
        </h3>
      </div>
    );
  }

  return (
    <>
      <div id={homeSiblingId} />
      <section
        ref={lveRef}
        className={classNames(
          pkgClassName,
          {
            'live-video-embed--is-rail': isRail,
            'live-video-embed--today-all-day': isTodayAllDay,
            'live-video-embed--msnbc-tve': isMSNBCTVE,
            'live-video-embed--news-now': isNewsNOW,
            'live-video-embed--noticias-ahora': isNoticiasAhora,
            'live-video-embed--telemundo-al-dia': isTelemundoAlDia,
            'live-video-embed--telemundo-deportes-ahora': isTelemundoDeportesAhora,
            'live-video-embed--enabled-on-news-cover': isEnabledOnNewsCover || isEnabledOnSports,
            'live-video-embed--enabled-on-telemundo-covers': isEnabledOnTelemundoCovers,
            'msnbc-new-logo': isMsnbcLogoUpdateEnabled && branding === STREAM_KEYS.MSNBC_TVE,
            'live-video-embed--movable': movePlayerInMobile,
          },
        )}
        data-packageid={content.id}
      >
        {(Logo || displayDek) && (
          <LinkIfHref
            href={destinationPageUrl}
            className={`${block}__link ${block}__block`}
          >
            {Logo && (isCoreVideoPlayer || playmakerOnlyLiveLogo) && (
              <div className={`${block}__block ${block}__logo-container`}>
                {isEnabledOnNewsCover ? (
                  <LinkIfHref className={`${block}__logo-link`} href="https://www.nbcnews.com/now">
                    <Logo />
                  </LinkIfHref>
                ) : <Logo />}
                {(isRail && !isNewsNOW && !isMSNBCTVE && !isSportsNow) && (
                  <hr className={`${block}__logo-line`} />
                )}
              </div>
            )}
            {displayDek && (
              <div className={`${block}__block ${block}__tagline`}>
                {dekText}
              </div>
            )}
          </LinkIfHref>
        )}
        {isMSNBCLiveVideo && !bypassAuth && (
          <MSNBCMetadata
            hasTempPass={hasTempPass}
            authWithProvider={authWithProvider}
            showProviderSelect={showProviderSelect}
            providerSelectVisible={providerSelectVisible}
          />
        )}
        <div
          ref={ref}
          className={classNames(
            `${block}__block ${block}__aspect-ratio-container`,
            {
              'msnbc-container': isMSNBCLiveVideo && !isRail,
            },
          )}
        >
          {shouldRenderByBreakpoint && (
            // rendering the surrounding markup in order to minimize CLS on >=large breakpoints
            <div className={`${block}__container`}>
              {inView && isMSNBCLiveVideo && !bypassAuth && (
                <>
                  <WithTVEAuthProvider
                    iframeRef={iframeRef}
                  />
                  {videoError !== ERROR_TYPES.BROWSER && (
                    <TempPass
                      checkTempPass={checkTempPass}
                    />
                  )}
                </>
              )}
              {isCoreVideoPlayer ? (
                <CoreVideoPlayerEmbed
                  playmakerMetadata={playmakerMetadata}
                  isMSNBC={isMSNBCTVE}
                  isRail={isRail}
                  authenticated={authenticated}
                  bypassAuth={bypassAuth}
                />
              ) : (
                <NDPVideo item={item} isRail={isRail} />
              )}
            </div>
          )}
        </div>
        <div className={`${block}__block ${block}__flag-and-headline`}>
          <LinkIfHref href={destinationPageUrl} className={`${block}__link`}>
            {isCoreVideoPlayer
              && <InlineBlinkingLiveDot className={`${block}__inline-blinking-live-dot`} />}
            {isCoreVideoPlayer || isLiveNDPVideo
              ? (
                <span
                  className={classNames(`${block}__eyebrow`, {
                    [`${block}__eyebrow--today-all-day`]: isTodayAllDay,
                  })}
                >
                  {eyebrowText}
              &nbsp;/&nbsp;
                </span>
              )
              : null}
            <h2
              className={classNames(`${block}__headline`, {
                [`${block}__headline--today-all-day`]: isTodayAllDay,
              })}
            >
              {titleToDisplay}
            </h2>
          </LinkIfHref>
        </div>
      </section>
    </>
  );
}

LiveVideoEmbed.propTypes = {
  authenticated: PropTypes.bool,
  content: PackagePropType.isRequired,
  currentTVEProgram: PropTypes.shape({
    title: PropTypes.string,
  }),
  hasTempPass: PropTypes.bool,
  shouldRenderByBreakpoint: PropTypes.bool,
  pkgClassName: PropTypes.string,
  providerSelectVisible: PropTypes.bool,
  hasCurrentShow: PropTypes.bool,
  showProviderSelect: PropTypes.func,
  hideProviderSelect: PropTypes.func,
  videoError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  loadTVESchedule: PropTypes.func,
};


/**
 *
 * @param {object} root0
 * @param {tveMp4} root0.tve
 * @param {Video} root0.video
 */
const mapStateToProps = ({ tve, video }) => ({
  authenticated: tve.authenticated,
  hasTempPass: tve.hasTempPass,
  providerSelectVisible: tve.providerSelectVisible,
  videoError: video.error,
  currentTVEProgram: {
    title: tve?.epgCurrent?.program?.title,
  },
});

/**
 * Maps dispatch actions to props for the LiveVideoEmbed component.
 *
 * @param {Function} dispatch - The Redux dispatch function
 */
const mapActionsToProps = (dispatch) => ({
  /**
   * Dispatches the action to hide the provider select.
   */
  hideProviderSelect: () => dispatch(hideProviderSelectAction()),
  /**
   * Dispatches the action to show the provider select.
   */
  showProviderSelect: () => dispatch(showProviderSelectAction()),
  /**
   * Dispatches the action to load the TVE schedule for a given vertical.
   *
   * @param {VerticalType} vertical - The vertical for which to load the schedule
   */
  loadTVESchedule: (vertical) => dispatch(loadTVEScheduleAction(vertical)),
});

const LiveVideoEmbedWrapper = connect(mapStateToProps, mapActionsToProps)(LiveVideoEmbed);
export { LiveVideoEmbedWrapper as LiveVideoEmbed };
